import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

const Timeline = ({ data, onItemClick }) => {
  const svgRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    if (!data || data.length === 0) return;

    const container = containerRef.current;
    const width = container.offsetWidth;
    const rowHeight = 40;
    const margin = { top: 60, right: 120, bottom: 20, left: 200 };
    const chartHeight = data.length * rowHeight;
    const height = chartHeight + margin.top + margin.bottom;

    // Clear existing SVG
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Parse dates and find extent
    const allDates = data.flatMap(d => [
      d.start_date ? new Date(d.start_date) : null,
      d.end_date ? new Date(d.end_date) : null
    ]).filter(d => d !== null);

    if (allDates.length === 0) {
      g.append('text')
        .attr('x', (width - margin.left - margin.right) / 2)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .attr('class', 'text-sm fill-gray-500')
        .text('No timeline data available');
      return;
    }

    const minDate = d3.min(allDates);
    const maxDate = d3.max(allDates);

    // Create time scale (weekly)
    const xScale = d3.scaleTime()
      .domain([minDate, maxDate])
      .range([0, width - margin.left - margin.right]);

    // Add time axis (weeks)
    const xAxis = d3.axisTop(xScale)
      .ticks(d3.timeWeek.every(1))
      .tickFormat(d3.timeFormat('%b %d'));

    g.append('g')
      .attr('class', 'x-axis')
      .call(xAxis)
      .selectAll('text')
      .attr('class', 'text-xs fill-gray-600')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    // Style axis
    g.selectAll('.x-axis path, .x-axis line')
      .attr('stroke', '#e5e7eb');

    // Draw rows
    data.forEach((item, index) => {
      const y = index * rowHeight + rowHeight / 2;

      // Row background
      g.append('rect')
        .attr('x', -margin.left)
        .attr('y', index * rowHeight)
        .attr('width', width)
        .attr('height', rowHeight - 1)
        .attr('fill', index % 2 === 0 ? '#ffffff' : '#f9fafb')
        .attr('class', 'cursor-pointer hover:fill-gray-100 transition-colors')
        .attr('role', 'button')
        .attr('tabindex', '0')
        .attr('aria-label', `View details for ${item.name || 'item'}`)
        .on('click', () => onItemClick && onItemClick(item))
        .on('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onItemClick && onItemClick(item);
          }
        });

      // Row label
      g.append('text')
        .attr('x', -10)
        .attr('y', y)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('class', 'text-sm font-medium fill-gray-900 cursor-pointer')
        .text(item.name || 'Unnamed')
        .on('click', () => onItemClick && onItemClick(item));

      // Timeline bar
      if (item.start_date && item.end_date) {
        const startDate = new Date(item.start_date);
        const endDate = new Date(item.end_date);
        const barX = xScale(startDate);
        const barWidth = Math.max(xScale(endDate) - barX, 2);

        // Bar group for hover effects
        const barGroup = g.append('g')
          .attr('class', 'timeline-bar-group')
          .attr('role', 'button')
          .attr('tabindex', '0')
          .attr('aria-label', `${item.name}: ${item.percent_complete || 0}% complete, status: ${item.status}`)
          .style('cursor', 'pointer')
          .on('click', () => onItemClick && onItemClick(item))
          .on('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onItemClick && onItemClick(item);
            }
          });

        // Bar background
        barGroup.append('rect')
          .attr('x', barX)
          .attr('y', y - 12)
          .attr('width', barWidth)
          .attr('height', 24)
          .attr('rx', 4)
          .attr('fill', item.color || '#3B82F6')
          .attr('opacity', 0.3)
          .attr('class', 'transition-all duration-200')
          .on('mouseenter', function() {
            d3.select(this).attr('opacity', 0.5);
          })
          .on('mouseleave', function() {
            d3.select(this).attr('opacity', 0.3);
          });

        // Completion indicator
        if (item.percent_complete) {
          const completionWidth = (barWidth * item.percent_complete) / 100;
          barGroup.append('rect')
            .attr('x', barX)
            .attr('y', y - 12)
            .attr('width', completionWidth)
            .attr('height', 24)
            .attr('rx', 4)
            .attr('fill', item.color || '#3B82F6')
            .attr('opacity', 0.9)
            .attr('class', 'transition-all duration-200')
            .on('mouseenter', function() {
              d3.select(this).attr('opacity', 1);
            })
            .on('mouseleave', function() {
              d3.select(this).attr('opacity', 0.9);
            });
        }

        // Tooltip on hover
        barGroup.append('title')
          .text(`${item.name}\nStatus: ${item.status}\nProgress: ${item.percent_complete || 0}%\n${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`);

        // Status badge
        g.append('text')
          .attr('x', barX + barWidth + 5)
          .attr('y', y)
          .attr('dominant-baseline', 'middle')
          .attr('class', 'text-xs font-medium fill-gray-700')
          .text(`${item.percent_complete || 0}%`);
      }
    });

  }, [data, onItemClick]);

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <svg ref={svgRef} className="w-full"></svg>
    </div>
  );
};

export default Timeline;
